import { useEffect, useState } from "react";
import { Clock, Users, CalendarPlus, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { todayAST } from "@/lib/date";
import DocumentFields, { validateDocument } from "@/components/DocumentFields";

const today = todayAST;

export default function NewAppointmentDialog({ doctorId, open, onOpenChange, onDone }) {
  const [mode, setMode] = useState("registered");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selPatient, setSelPatient] = useState(null);
  const [walkIn, setWalkIn] = useState({ name: "", phone: "" });
  const [doc, setDoc] = useState({ type: "", number: "" });
  const [date, setDate] = useState(today());
  const [blocks, setBlocks] = useState([]);
  const [selBlock, setSelBlock] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setMode("registered"); setQuery(""); setResults([]); setSelPatient(null);
    setWalkIn({ name: "", phone: "" }); setDoc({ type: "", number: "" }); setDate(today()); setBlocks([]);
    setSelBlock(null); setSelSlot(null);
  };

  const loadSlots = async (d) => {
    setSelBlock(null); setSelSlot(null);
    const { data } = await api.get(`/doctors/${doctorId}/slots`, { params: { date: d } });
    setBlocks(data);
  };

  useEffect(() => {
    if (open) { reset(); loadSlots(today()); }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => { if (open) loadSlots(date); /* eslint-disable-next-line */ }, [date]);

  const searchPatients = async () => {
    const { data } = await api.get("/patients/search", { params: { q: query } });
    setResults(data);
  };

  const confirm = async () => {
    if (mode === "registered" && !selPatient) { toast.error("Selecciona un paciente"); return; }
    if (mode === "walkin" && !walkIn.name.trim()) { toast.error("Ingresa el nombre del paciente"); return; }
    if (mode === "walkin") {
      const docErr = validateDocument(doc.type, doc.number);
      if (docErr) { toast.error(docErr); return; }
    }
    if (!selBlock) { toast.error("Selecciona un bloque disponible"); return; }
    if (selBlock.modality === "HORAS" && !selSlot) { toast.error("Selecciona una hora"); return; }
    setBusy(true);
    try {
      await api.post("/appointments/staff", {
        availability_id: selBlock.id,
        slot_time: selBlock.modality === "HORAS" ? selSlot : null,
        patient_id: mode === "registered" ? selPatient.user_id : null,
        walk_in_name: mode === "walkin" ? walkIn.name.trim() : null,
        walk_in_phone: mode === "walkin" ? walkIn.phone.trim() : null,
        walk_in_document_type: mode === "walkin" ? doc.type : null,
        walk_in_document_number: mode === "walkin" ? doc.number : null,
      });
      toast.success("Cita agendada");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl bg-white" data-testid="new-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-[#0284C7]" /> Agendar cita</DialogTitle>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="grid w-full grid-cols-2 rounded-full bg-slate-100 p-1">
            <TabsTrigger value="registered" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-registered-patient">Paciente registrado</TabsTrigger>
            <TabsTrigger value="walkin" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-walkin-patient">Sin cuenta</TabsTrigger>
          </TabsList>

          <TabsContent value="registered">
            <div className="mt-3 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchPatients())} placeholder="Buscar por nombre, correo o documento…" className="rounded-xl border-[#E2E8F0] pl-10" data-testid="patient-search-query" />
              </div>
              <Button type="button" onClick={searchPatients} className="rounded-xl bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="patient-search-btn">Buscar</Button>
            </div>
            <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
              {results.map((p) => (
                <button key={p.user_id} type="button" onClick={() => setSelPatient(p)} className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left ${selPatient?.user_id === p.user_id ? "border-[#0284C7] bg-sky-50/40" : "border-[#E2E8F0]"}`} data-testid={`patient-result-${p.user_id}`}>
                  <div>
                    <div className="text-sm font-extrabold text-slate-900">{p.name}</div>
                    <div className="text-xs font-bold text-slate-400">{p.email}</div>
                  </div>
                  {selPatient?.user_id === p.user_id && <span className="text-xs font-bold text-[#0284C7]">Seleccionado</span>}
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="walkin">
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500"><UserPlus className="h-3.5 w-3.5" /> Paciente sin cuenta (walk-in)</div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nombre *</Label><Input value={walkIn.name} onChange={(e) => setWalkIn((w) => ({ ...w, name: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="walkin-name" /></div>
              <DocumentFields type={doc.type} number={doc.number} onChange={setDoc} prefix="walkin-doc" />
              <p className="text-xs font-semibold text-slate-400">Si el documento ya existe, la cita se asocia a ese paciente.</p>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label><Input value={walkIn.phone} onChange={(e) => setWalkIn((w) => ({ ...w, phone: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="walkin-phone" /></div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Fecha</Label>
          <Input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 w-48 rounded-xl border-[#E2E8F0]" data-testid="new-appt-date" />
        </div>

        <div className="mt-3 space-y-3">
          {blocks.length === 0 && <p className="rounded-xl border border-dashed border-[#E2E8F0] p-6 text-center text-sm font-bold text-slate-400" data-testid="new-appt-no-slots">Sin disponibilidad para esta fecha.</p>}
          {blocks.map((b) => (
            <div key={b.id} className={`rounded-xl border p-4 ${selBlock?.id === b.id ? "border-[#0284C7] bg-sky-50/40" : "border-[#E2E8F0]"}`} data-testid={`na-block-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  {b.modality === "HORAS" ? <Clock className="h-4 w-4 text-[#0284C7]" /> : <Users className="h-4 w-4 text-[#0284C7]" />}
                  {b.start_time}–{b.end_time} · {b.center_name}
                </div>
                <span className="rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-bold text-slate-500">
                  {b.modality === "HORAS" ? "Hora fija" : `${b.remaining_cupos} cupos`}
                </span>
              </div>
              {b.modality === "HORAS" ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {(b.available_slots || []).length === 0 && <span className="text-xs font-bold text-slate-400">Sin horas libres</span>}
                  {(b.available_slots || []).map((s) => (
                    <button key={s} type="button" onClick={() => { setSelBlock(b); setSelSlot(s); }} data-testid={`na-slot-${b.id}-${s}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold ${selBlock?.id === b.id && selSlot === s ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <button type="button" onClick={() => { setSelBlock(b); setSelSlot(null); }} disabled={b.remaining_cupos <= 0} className={`mt-3 rounded-full border px-4 py-1.5 text-xs font-bold ${selBlock?.id === b.id ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600"}`} data-testid={`na-pick-${b.id}`}>
                  {selBlock?.id === b.id ? "Seleccionado" : "Seleccionar (por llegada)"}
                </button>
              )}
            </div>
          ))}
        </div>

        <Button onClick={confirm} disabled={busy} className="mt-4 w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="new-appt-confirm">
          {busy ? "Agendando…" : "Agendar cita"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
